import { Check, Zap } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { HttpActionDto } from '@/Domain/api/Action/dto/Action.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { AnimatedCollapsibleContent } from '@/Domain/components/Shared/Collapsible/Collapsible';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { useUpdateActionDetails } from '@/Domain/hooks/Action/ApiAction/useUpdateActionDetails.hook';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

// A valid Action left inactive is silently dropped from the agent's toolset, so
// without this prompt users finish configuring and never switch it on.
const SUCCESS_DWELL_MS = 750;

type Props = {
  action: HttpActionDto;
};

export const ActivationBanner = ({ action }: Props) => {
  const { t } = useTranslation('actions_page');
  const { handleUpdateDetails, updateActionErrorMessage, isActionPending } =
    useUpdateActionDetails();
  const { capture } = usePostHogAnalytics();

  const eligible = action.id != null && action.is_valid && !action.is_active;

  const [open, setOpen] = useState(eligible);
  const [succeeded, setSucceeded] = useState(false);
  const dismissTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (eligible && !succeeded) setOpen(true);
  }, [eligible, succeeded]);

  useEffect(() => () => clearTimeout(dismissTimer.current), []);

  const hasTrackedShown = useRef(false);
  useEffect(() => {
    if (!eligible || hasTrackedShown.current) return;
    hasTrackedShown.current = true;
    capture(PostHogEventEnum.ActionEnablePromptShown, {
      action_id: action.id,
      surface: 'sheet_banner',
    });
  }, [eligible, capture, action.id]);

  const handleEnable = async () => {
    try {
      await handleUpdateDetails(action.id, { is_active: true }, false);
      capture(PostHogEventEnum.ActionEnabledFromPrompt, {
        action_id: action.id,
        surface: 'sheet_banner',
      });
      setSucceeded(true);
      dismissTimer.current = setTimeout(() => setOpen(false), SUCCESS_DWELL_MS);
    } catch (error) {
      toast.error(updateActionErrorMessage(error));
    }
  };

  return (
    <AnimatedCollapsibleContent open={open} className="pb-7">
      <div
        data-testid="action-sheet-activation-banner"
        className="flex flex-col gap-3 rounded-xl border border-info/20 bg-gradient-to-b from-info-muted/40 to-card p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="flex items-start gap-3 sm:items-center">
          <span
            className={`grid size-9 shrink-0 place-items-center rounded-full ${
              succeeded ? 'bg-success/10 text-success' : 'bg-info/10 text-info'
            }`}
          >
            {succeeded ? <Check className="size-4" /> : <Zap className="size-4" />}
          </span>
          <div className="space-y-0.5">
            <p className="text-sm font-medium text-foreground">
              {succeeded ? t('tooltip.action_enabled') : t('sheet.activation_banner.title')}
            </p>
            {!succeeded && (
              <p className="text-sm text-muted-foreground">
                {t('sheet.activation_banner.description')}
              </p>
            )}
          </div>
        </div>
        {!succeeded && (
          <Button
            data-testid="action-sheet-enable-button"
            size="sm"
            className="shrink-0"
            onClick={handleEnable}
            disabled={isActionPending}
          >
            {t('sheet.button.enable')}
          </Button>
        )}
      </div>
    </AnimatedCollapsibleContent>
  );
};
